/**
 * Offboarding constants
 *
 * Cross-service constants for the per-user offboarding pipeline. Offboarding is
 * per-contractor and per-contract: one unit of work = one contractor losing
 * standing on one contract. Several independent triggers converge on it, and
 * the `reason` records which one — it drives the UD offboard label and the
 * notification/audit copy, but never the mechanics of the teardown.
 */

/**
 * Why a contractor is being offboarded from a contract.
 *
 * Carried on the `roster.member.offboard` event and on `roster.member.removed`.
 * - `CONTRACT_EXPIRED`         — contract reached the end of its period of performance.
 * - `CONTRACT_TERMINATED`      — contract cancelled before its natural end date.
 * - `ROSTER_REMOVAL_IMMEDIATE` — Roster Owner/OPERATIONS/ADMIN removed the member, effective now.
 * - `ROSTER_REMOVAL_SCHEDULED` — a future-dated removal that has come due.
 */
export enum OffboardReason {
  CONTRACT_EXPIRED = 'CONTRACT_EXPIRED',
  CONTRACT_TERMINATED = 'CONTRACT_TERMINATED',
  ROSTER_REMOVAL_IMMEDIATE = 'ROSTER_REMOVAL_IMMEDIATE',
  ROSTER_REMOVAL_SCHEDULED = 'ROSTER_REMOVAL_SCHEDULED',
}

/** All offboard reason values, for schema/validation use. */
export const OFFBOARD_REASON_VALUES = Object.values(OffboardReason) as [OffboardReason, ...OffboardReason[]];

/**
 * Event topic for the dedicated per-user offboard stream, following the
 * `{domain}.{entity}.{action}` convention.
 */
export const ROSTER_MEMBER_OFFBOARD_EVENT = 'roster.member.offboard' as const;

/**
 * Payload carried on a `roster.member.offboard` event.
 * One event = one contractor losing standing on one contract.
 */
export interface RosterMemberOffboardPayload {
  /** The contractor being offboarded. */
  userId: string;
  /** The contract they are losing standing on. */
  contractId: string;
  /** Which trigger started offboarding. */
  reason: OffboardReason;
  /** Actor — a specific user id for UI removals, or the system user id for scheduled/contract-driven paths. */
  triggeredBy: string;
  /** Effective date of the removal (for scheduled and contract-driven reasons), ISO date string. */
  scheduledDate?: string;
}
